// Illustrated prize art, matched from the prize name. Self-contained SVG so
// every prize looks real even before a parent adds a photo; image_url always
// wins when present.

import type { ReactElement } from "react";

interface Motif {
  test: RegExp;
  render: () => ReactElement;
}

const stroke = "none";

const MOTIFS: Motif[] = [
  { test: /plane|flight|flying|pilot|left seat|certificate/i, render: () => (
    <g>
      <path d="M8 36 L34 28 L52 10 C55 7 59 9 57 13 L46 32 L54 44 L48 46 L40 36 L28 40 L30 48 L25 49 L20 41 Z" fill="#47a6ff" />
      <circle cx="14" cy="14" r="4" fill="#ffcc56" />
      <circle cx="24" cy="9" r="2" fill="#ffe9a8" />
    </g>
  ) },
  { test: /lego|brick|build/i, render: () => (
    <g>
      <rect x="10" y="30" width="26" height="16" rx="2" fill="#ff6b3d" />
      <rect x="14" y="24" width="7" height="6" rx="2" fill="#ff6b3d" />
      <rect x="25" y="24" width="7" height="6" rx="2" fill="#ff6b3d" />
      <rect x="30" y="14" width="22" height="14" rx="2" fill="#ffcc56" />
      <rect x="33" y="9" width="6" height="5" rx="2" fill="#ffcc56" />
      <rect x="43" y="9" width="6" height="5" rx="2" fill="#ffcc56" />
    </g>
  ) },
  { test: /ice cream|dessert|treat|snack|pizza|breakfast|dinner|takeout|boba|coffee/i, render: () => (
    <g>
      <path d="M22 26 A10 10 0 0 1 42 26 Z" fill="#ff9fb1" />
      <circle cx="27" cy="19" r="6" fill="#ffe9a8" />
      <circle cx="37" cy="19" r="6" fill="#c9f0d8" />
      <path d="M24 27 L32 50 L40 27 Z" fill="#e0a96d" />
      <circle cx="32" cy="14" r="2.4" fill="#e5484d" />
    </g>
  ) },
  { test: /game|video|controller|in-game|screen time|app /i, render: () => (
    <g>
      <rect x="10" y="20" width="44" height="24" rx="12" fill="#8f9bb3" />
      <rect x="18" y="28" width="10" height="3.4" rx="1.7" fill="#eef2f8" />
      <rect x="21.3" y="24.7" width="3.4" height="10" rx="1.7" fill="#eef2f8" />
      <circle cx="43" cy="27" r="2.6" fill="#ffcc56" />
      <circle cx="48" cy="32" r="2.6" fill="#31c58d" />
    </g>
  ) },
  { test: /movie|film|theater|watch/i, render: () => (
    <g>
      <rect x="12" y="22" width="40" height="26" rx="4" fill="#2b3a55" />
      <path d="M12 22 L52 22 L48 12 L8 12 Z" fill="#47a6ff" />
      <circle cx="16" cy="17" r="2.4" fill="#fff" />
      <circle cx="28" cy="17" r="2.4" fill="#fff" />
      <circle cx="40" cy="17" r="2.4" fill="#fff" />
      <path d="M28 30 L38 35 L28 40 Z" fill="#ffcc56" />
    </g>
  ) },
  { test: /cash|\$|money|fund|budget|spend|shopping|store|mall/i, render: () => (
    <g>
      <rect x="10" y="18" width="44" height="26" rx="4" fill="#31c58d" />
      <circle cx="32" cy="31" r="8" fill="#c9f0d8" />
      <text x="32" y="35.5" textAnchor="middle" fontSize="12" fontWeight="800" fill="#137a52">$</text>
      <circle cx="17" cy="24" r="2" fill="#c9f0d8" />
      <circle cx="47" cy="38" r="2" fill="#c9f0d8" />
    </g>
  ) },
  { test: /book|read|library|bookstore/i, render: () => (
    <g>
      <path d="M32 16 C26 12 16 12 12 15 L12 46 C16 43 26 43 32 47 C38 43 48 43 52 46 L52 15 C48 12 38 12 32 16 Z" fill="#a78bfa" />
      <path d="M32 16 L32 47" stroke="#7c5cd6" strokeWidth="2.5" fill="none" />
      <path d="M17 22 C21 20.5 26 21 29 23 M17 29 C21 27.5 26 28 29 30" stroke="#efe9ff" strokeWidth="2" fill="none" strokeLinecap="round" />
    </g>
  ) },
  { test: /sleepover|bedtime|stay up|late-night|curfew|blanket|fort/i, render: () => (
    <g>
      <path d="M43 12 A14 14 0 1 0 52 32 A11.5 11.5 0 0 1 43 12 Z" fill="#ffcc56" />
      <circle cx="17" cy="16" r="2" fill="#ffe9a8" />
      <circle cx="24" cy="26" r="1.5" fill="#ffe9a8" />
      <circle cx="14" cy="34" r="1.6" fill="#ffe9a8" />
      <rect x="10" y="42" width="44" height="8" rx="4" fill="#47a6ff" />
    </g>
  ) },
  { test: /trip|zoo|museum|park|outing|adventure|destination|weekend|travel|city|camp|hike|summit|mountain|rock|falls|overlook|cavern|tubing|rafting|zip/i, render: () => (
    <g>
      <path d="M6 46 L22 22 L32 36 L40 26 L58 46 Z" fill="#31c58d" />
      <path d="M22 22 L27 29 L18 29 Z" fill="#e6f7ef" />
      <circle cx="46" cy="14" r="6" fill="#ffcc56" />
    </g>
  ) },
  { test: /ticket|concert|event|sporting|game ticket/i, render: () => (
    <g>
      <path d="M10 24 L54 18 L56 30 A4 4 0 0 0 56 38 L58 44 L14 50 L12 40 A4 4 0 0 0 12 32 Z" fill="#ff6b3d" transform="rotate(-4 32 32)" />
      <path d="M24 26 L26 44" stroke="#ffd9c9" strokeWidth="2.4" strokeDasharray="3 4" fill="none" />
      <circle cx="42" cy="32" r="5" fill="#ffd9c9" />
    </g>
  ) },
  { test: /chore|skip|pass|unscheduled|free/i, render: () => (
    <g>
      <circle cx="32" cy="32" r="20" fill="#c9f0d8" />
      <path d="M22 33 L29 40 L43 25" stroke="#137a52" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </g>
  ) },
  { test: /friend|playdate|guest/i, render: () => (
    <g>
      <circle cx="24" cy="24" r="7" fill="#ffb3a0" />
      <path d="M13 46 C13 37 35 37 35 46 Z" fill="#47a6ff" />
      <circle cx="42" cy="26" r="6" fill="#e0a96d" />
      <path d="M32 46 C32 38 52 38 52 46 Z" fill="#31c58d" />
    </g>
  ) },
  { test: /bike|scooter|skate/i, render: () => (
    <g>
      <circle cx="18" cy="40" r="9" fill="none" stroke="#8f9bb3" strokeWidth="3" />
      <circle cx="46" cy="40" r="9" fill="none" stroke="#8f9bb3" strokeWidth="3" />
      <path d="M18 40 L28 24 L40 24 L46 40 M28 24 L34 40 L18 40" stroke="#ff6b3d" strokeWidth="3" fill="none" strokeLinecap="round" />
    </g>
  ) },
  { test: /gear|headphone|camera|tech|laptop|upgrade|room/i, render: () => (
    <g>
      <path d="M14 38 A18 18 0 0 1 50 38" stroke="#2b3a55" strokeWidth="4" fill="none" />
      <rect x="10" y="36" width="9" height="14" rx="4" fill="#47a6ff" />
      <rect x="45" y="36" width="9" height="14" rx="4" fill="#47a6ff" />
    </g>
  ) },
  { test: /star|award|grand|ceremony/i, render: () => (
    <g>
      <path d="M32 8 L38 24 L55 24 L41 34 L46 51 L32 41 L18 51 L23 34 L9 24 L26 24 Z" fill="#ffcc56" />
      <circle cx="32" cy="30" r="4" fill="#fff5d6" />
    </g>
  ) },
];

const FALLBACK = (
  <g>
    <rect x="12" y="24" width="40" height="24" rx="4" fill="#ff6b3d" />
    <rect x="12" y="18" width="40" height="8" rx="3" fill="#ffcc56" />
    <rect x="29" y="18" width="6" height="30" fill="#fff1e9" />
    <path d="M32 16 C26 8 16 12 22 18 M32 16 C38 8 48 12 42 18" stroke="#e5484d" strokeWidth="3" fill={stroke} strokeLinecap="round" />
  </g>
);

export function PrizeArt({ name }: { name: string }): ReactElement {
  const motif = MOTIFS.find((entry) => entry.test.test(name));
  return (
    <svg viewBox="0 0 64 64" className="prize-art" aria-hidden="true">
      {motif ? motif.render() : FALLBACK}
    </svg>
  );
}

// Fallback descriptions keep cards from feeling empty before parents write
// their own. Written to make the prize feel near and concrete.
export function fallbackDescription(name: string, band: string | null): string {
  if (/plane|flight|flying/i.test(name)) return "A real airplane, a real headset, and you in it. The big one.";
  if (/lego/i.test(name)) return "You pick the set. We watch you build it.";
  if (/screen time/i.test(name)) return "A full extra hour, guilt-free, no questions asked.";
  if (/sleepover|friend/i.test(name)) return "Your friend, our snacks. Done deal.";
  if (/movie/i.test(name)) return "Your pick, your dessert, whole family watches.";
  if (/cash|\$/i.test(name)) return "Real money, no strings. Spend it your way.";
  if (/trip|day|weekend|outing|adventure/i.test(name)) return "You plan it, we drive. Your call from start to finish.";
  if (/chore/i.test(name)) return "One chore, officially somebody else's problem.";
  if (/book|read/i.test(name)) return "Any book you want. No budget argument.";
  if (band === "A") return "Earn your stars and this one is yours.";
  return "Save up and claim it. You earned every bit.";
}
